/**
 * Structured Document Editor
 * 
 * TipTap-based editor for aerospace JSON documents.
 * Replaces contentEditable iframe with structured, validated editing.
 */

import { useEditor, EditorContent } from '@tiptap/react';
import { TableBubbleMenu } from './TableBubbleMenu';
import StarterKit from '@tiptap/starter-kit';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Image from '@tiptap/extension-image';
import Heading from '@tiptap/extension-heading';
import Paragraph from '@tiptap/extension-paragraph';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import SearchAndReplace from '@sereneinserenade/tiptap-search-and-replace';
import { useEffect, useState, useRef, useCallback } from 'react';
import type { AeroDocument, Section } from '../../types/document-schema';
import type { DocumentOperation } from '../../types/document-operations';
import { EditorToolbar } from './EditorToolbar';
import { EditorSidebar } from './EditorSidebar';
import { EditorSearch } from './EditorSearch';
import { VersionHistory } from './VersionHistory';
import { VersionCompare } from './VersionCompare';
import { DiffHighlight } from './DiffHighlight';
import { VectorGraphicNode } from './extensions/VectorGraphicNode';
import { PlanTaskNode } from './extensions/PlanTaskNode';
import { convertTipTapToDocument } from '../../utils/tiptap-to-document';
import { 
  convertDocumentToTipTap,
  getStatusBadgeClass 
} from '../../utils/document-to-tiptap';
import { documentsApi, documentVersionApi, projectsApi } from '../../services/api';
import { logger } from '../../utils/logger';
import { useDocumentStreaming } from '../../hooks/useDocumentStreaming';
import { 
  useToast,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Label,
  RadixSelect,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui';

export interface StructuredEditorProps {
  document: AeroDocument;
  onUpdate: (operations: DocumentOperation[]) => void;
  onSave: () => void;
  readOnly?: boolean;
  documentTitle?: string;
  onBack?: () => void;
  saving?: boolean;
  error?: string | null;
  hasUnsavedChanges?: boolean;
  onToggleSplitView?: () => void;
  splitViewEnabled?: boolean;
  lastSaved?: Date | null;
  isOnline?: boolean;
  onAddSection?: (sectionType: 'section' | 'subsection' | 'appendix', title: string, parentId?: string) => void;
  editorRef?: React.MutableRefObject<Editor | null>;
  projectId?: string;
  documentNumber?: string;
  streamingOperations?: DocumentOperation[]; // AI operations to stream into editor
  onStreamingComplete?: () => void;
  onDocumentSyncFromServer?: (document: AeroDocument, s3VersionId?: string, preAIVersionId?: string) => void; // Called when server sends updated document
  onOpenPdfInViewer?: (pdfUrl: string, title: string) => void; // Open generated PDF in app viewer
}

export function StructuredEditor({ 
  document, 
  onUpdate, 
  onSave, 
  readOnly = false,
  documentTitle,
  onBack,
  saving,
  error,
  hasUnsavedChanges,
  onToggleSplitView,
  splitViewEnabled,
  lastSaved,
  isOnline,
  onAddSection,
  editorRef,
  projectId,
  documentNumber,
  streamingOperations: _streamingOperations,
  onStreamingComplete,
  onDocumentSyncFromServer,
  onOpenPdfInViewer
}: StructuredEditorProps) {
  // Check for invalid document structure (used at render time, not for early return to satisfy hooks rules)
  const isInvalidDocument = !document?.metadata || !document?.body;
  
  const [_lastSavedVersion, setLastSavedVersion] = useState(document?.version || '');
  const [documentVersion, setDocumentVersion] = useState(document?.version || '');
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isUpdatingFromPropRef = useRef(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [compareVersions, setCompareVersions] = useState<{v1: string; v2: string} | null>(null);
  const [isAIEditing, setIsAIEditing] = useState(false);
  const [showBlockIds, setShowBlockIds] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showRevisionOverride, setShowRevisionOverride] = useState(false);
  const [overrideRevision, setOverrideRevision] = useState(document?.metadata?.currentRevision || 'IR');
  const [isOverridingRevision, setIsOverridingRevision] = useState(false);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  // Unified skip-refresh state: tracks WHY the next version-change refresh should be skipped.
  // Replaces the previous two separate boolean refs (skipNextRefreshRef, isLocalSaveVersionChangeRef)
  // with a single typed state that makes the state machine explicit.
  //   null           = normal operation, version changes trigger editor refresh
  //   'section_update' = AI section update already applied content with highlights, skip refresh
  //   'local_save'     = user's local save bumped version, skip refresh to preserve cursor
  type SkipRefreshReason = null | 'section_update' | 'local_save';
  const skipRefreshReasonRef = useRef<SkipRefreshReason>(null);
  // Track pre-AI version for "Undo All AI Changes" functionality
  const [preAIVersionId, setPreAIVersionId] = useState<string | null>(null);
  // Checkpoint of TipTap JSON content before AI edits (set on FIRST document_sync per AI turn)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TipTap JSON content type is complex and untyped
  const preAIContentRef = useRef<Record<string, unknown> | null>(null);
  const [isRestoringVersion, setIsRestoringVersion] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const { success: showSuccess, error: showError } = useToast();
  
  // Handle revision override for Draft documents
  const handleRevisionOverride = useCallback(async () => {
    if (!projectId || !documentNumber || !overrideRevision) return;
    
    setIsOverridingRevision(true);
    try {
      await projectsApi.overrideRevision(projectId, documentNumber, overrideRevision);
      showSuccess(`Revision changed to ${overrideRevision}`);
      setShowRevisionOverride(false);
      // Reload the page to reflect the new revision
      window.location.reload();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to change revision';
      showError(message);
    } finally {
      setIsOverridingRevision(false);
    }
  }, [projectId, documentNumber, overrideRevision, showSuccess, showError]);
  
  // Convert JSON document to TipTap content format
  const initialContent = convertDocumentToTipTap(document);
  
  // Debounced update handler - convert TipTap changes back to JSON and notify parent
  const handleContentUpdate = useCallback((_editor: Editor) => {
    if (readOnly) {
      logger.debug('[StructuredEditor] Ignoring update: readOnly');
      return;
    }
    if (isUpdatingFromPropRef.current) {
      logger.debug('[StructuredEditor] Ignoring update: isUpdatingFromPropRef is true (external update in progress)');
      return;
    }
    
    // Debounce updates to avoid excessive processing while user is typing
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    updateTimeoutRef.current = setTimeout(() => {
      try {
        // Just notify parent that content changed
        logger.debug('[StructuredEditor] Content updated by user');
        
        // Only set the unsaved changes flag - don't convert to JSON yet
        // We'll do that on save to avoid triggering re-renders
        onUpdate([]);
      } catch (error) {
        logger.error('[StructuredEditor] Error in update handler:', error);
      }
    }, 500); // 500ms debounce - adjust based on performance needs
  }, [onUpdate, readOnly]);
  
  const editor = useEditor({
    // CRITICAL: Disable immediate render for React 18 Strict Mode compatibility
    // Without this, operations may be applied multiple times due to double-render
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: false, // Disable default heading to use custom one
        paragraph: false, // Disable default paragraph to use custom one
        bulletList: false, // Disable default bulletList to use custom one
        orderedList: false, // Disable default orderedList to use custom one
      }),
      Heading.configure({
        levels: [1, 2, 3, 4, 5, 6], // Support all 6 heading levels for deep section nesting
      }).extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            id: {
              default: null,
              parseHTML: element => element.getAttribute('id'),
              renderHTML: attributes => {
                if (!attributes.id) {
                  return {};
                }
                return { id: attributes.id };
              },
            },
          };
        },
      }),
      Paragraph.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            'data-block-id': {
              default: null,
              parseHTML: element => element.getAttribute('data-block-id'),
              renderHTML: attributes => {
                if (!attributes['data-block-id']) {
                  return {};
                }
                return { 'data-block-id': attributes['data-block-id'] };
              },
            },
          };
        },
      }),
      BulletList.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            'data-block-id': {
              default: null,
              parseHTML: element => element.getAttribute('data-block-id'),
              renderHTML: attributes => {
                if (!attributes['data-block-id']) {
                  return {};
                }
                return { 'data-block-id': attributes['data-block-id'] };
              },
            },
          };
        },
      }),
      OrderedList.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            'data-block-id': {
              default: null,
              parseHTML: element => element.getAttribute('data-block-id'),
              renderHTML: attributes => {
                if (!attributes['data-block-id']) {
                  return {};
                }
                return { 'data-block-id': attributes['data-block-id'] };
              },
            },
          };
        },
      }),
      Table.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            'data-block-id': {
              default: null,
              parseHTML: element => element.getAttribute('data-block-id'),
              renderHTML: attributes => {
                if (!attributes['data-block-id']) {
                  return {};
                }
                return { 'data-block-id': attributes['data-block-id'] };
              },
            },
          };
        },
      }).configure({
        resizable: true,
      }),
      TableRow.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            'data-row-id': {
              default: null,
              parseHTML: element => element.getAttribute('data-row-id'),
              renderHTML: attributes => {
                if (!attributes['data-row-id']) {
                  return {};
                }
                return { 'data-row-id': attributes['data-row-id'] };
              },
            },
          };
        },
      }),
      TableHeader.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            'data-col-id': {
              default: null,
              parseHTML: element => element.getAttribute('data-col-id'),
              renderHTML: attributes => {
                if (!attributes['data-col-id']) {
                  return {};
                }
                return { 'data-col-id': attributes['data-col-id'] };
              },
            },
          };
        },
      }),
      TableCell.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            'data-col-id': {
              default: null,
              parseHTML: element => element.getAttribute('data-col-id'),
              renderHTML: attributes => {
                if (!attributes['data-col-id']) {
                  return {};
                }
                return { 'data-col-id': attributes['data-col-id'] };
              },
            },
          };
        },
      }),
      Image,
      VectorGraphicNode, // Mermaid diagram support with rendered/code toggle
      PlanTaskNode, // Plan task block support for planning mode
      DiffHighlight, // Add diff highlighting for streaming edits
      SearchAndReplace.configure({
        searchResultClass: 'search-result',
        caseSensitive: false,
        disableRegex: true, // Disable regex for simpler user experience
      }),
    ],
    content: initialContent,
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      handleContentUpdate(editor);
    },
  });
  
  // Document streaming for inline AI edits
  const documentStreaming = useDocumentStreaming({
    editor,
    enabled: !readOnly,
    onOperationApplied: (operation) => {
      logger.debug('[StructuredEditor] Streaming operation applied:', operation.type);
      setIsAIEditing(true);
    },
    onStreamComplete: () => {
      logger.debug('[StructuredEditor] Streaming complete');
      setIsAIEditing(false);
      
      // Trigger update after all operations applied
      if (editor) {
        handleContentUpdate(editor);
      }
      if (onStreamingComplete) {
        onStreamingComplete();
      }
    },
    onDocumentSync: (_newDocument) => {
      logger.debug('[StructuredEditor] Document sync received from server');
      // The parent component can handle this if needed
    },
    // CRITICAL: Pass the suppress flag setter to prevent auto-save during streaming
    setSuppressAutoSave: (suppress: boolean) => {
      logger.debug('[StructuredEditor] setSuppressAutoSave:', suppress, '(from streaming)');
      isUpdatingFromPropRef.current = suppress;
    }
  });
  
  // Register server-side operation handlers on window object
  useEffect(() => {
    // These handlers are called directly by WorkspacePage when server-side events arrive
    window.__handleSectionUpdate = (sectionId: string, section: Section, operationType: string, changedBlockIds?: string[]) => {
      logger.debug('[StructuredEditor] Server section update:', sectionId, operationType, 'changedBlocks:', changedBlockIds);
      setIsAIEditing(true);
      // Mark that we're handling a section update - skip the next version-based refresh
      // because the TipTap editor already has the correct (highlighted) content
      skipRefreshReasonRef.current = 'section_update';
      documentStreaming.handleSectionUpdate(sectionId, section, operationType, changedBlockIds);
    };
    
    window.__handleSectionDelete = (sectionId: string) => {
      logger.debug('[StructuredEditor] Server section delete:', sectionId);
      setIsAIEditing(true);
      documentStreaming.handleSectionDelete(sectionId);
    };
    
    window.__handleDocumentSync = (newDocument: AeroDocument, version: string, s3VersionId?: string, preAIVersionIdFromServer?: string) => {
      logger.debug('[StructuredEditor] Server document sync:', version, 's3VersionId:', s3VersionId, 'preAIVersionId:', preAIVersionIdFromServer);
      setIsAIEditing(false);
      
      // CRITICAL FIX: Clear skip-refresh state so the document_sync triggers a full editor refresh
      // Previous section_update events set this, but document_sync means the full document is final
      // and we need to ensure TipTap reflects the complete server state
      skipRefreshReasonRef.current = null;
      
      // CRITICAL: Directly update TipTap editor with the new document content
      // This ensures the editor always reflects the server's document state after AI edits
      if (editor && newDocument) {
        logger.debug('[StructuredEditor] Forcing TipTap update with synced document');
        isUpdatingFromPropRef.current = true;
        
        // Checkpoint pre-AI content on FIRST document_sync per turn only
        if (!preAIContentRef.current && editor) {
          preAIContentRef.current = editor.getJSON();
          logger.debug('[StructuredEditor] Checkpointed pre-AI TipTap content');
        }
        
        const newContent = convertDocumentToTipTap(newDocument);
        editor.commands.setContent(newContent, false);
        
        setDocumentVersion(newDocument.version);
        setLastSavedVersion(newDocument.version);
        
        setTimeout(() => {
          isUpdatingFromPropRef.current = false;
        }, 500);
      }
      
      documentStreaming.handleDocumentSync(newDocument, version, s3VersionId, preAIVersionIdFromServer);
      
      // Store the pre-AI version ID for "Undo All AI Changes" button
      if (preAIVersionIdFromServer) {
        logger.debug('[StructuredEditor] Setting preAIVersionId for undo:', preAIVersionIdFromServer);
        setPreAIVersionId(preAIVersionIdFromServer);
      }
      
      // CRITICAL: Update parent's document state with server's document
      // This ensures the correct document structure is saved, not the TipTap state
      if (onDocumentSyncFromServer && newDocument) {
        logger.debug('[StructuredEditor] Updating parent document state from server, s3VersionId:', s3VersionId, 'preAIVersionId:', preAIVersionIdFromServer);
        onDocumentSyncFromServer(newDocument, s3VersionId, preAIVersionIdFromServer);
      }
      
      if (onStreamingComplete) {
        onStreamingComplete();
      }
    };
    
    return () => {
      delete window.__handleSectionUpdate;
      delete window.__handleSectionDelete;
      delete window.__handleDocumentSync;
    };
  }, [documentStreaming, editor, onStreamingComplete, onDocumentSyncFromServer]);
  
  // NOTE: Legacy client-side streaming operations removed.
  // All AI document edits now use server-side section_update events.
  // See useDocumentStreaming.handleSectionUpdate() for the active path.
  
  // Update editable state when readOnly changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly);
      logger.debug('[StructuredEditor] Editable state updated:', !readOnly);
      
      // Expose editor instance to parent via ref
      // Also attach helper method to skip refresh on local save
      if (editorRef) {
        editorRef.current = editor;
        // Attach method to skip version refresh (called before local save updates version)
        // Using type assertion to extend Editor interface for this custom method
        (editor as Editor & { skipNextVersionRefresh?: () => void }).skipNextVersionRefresh = () => {
          skipRefreshReasonRef.current = 'local_save';
          logger.debug('[StructuredEditor] skipNextVersionRefresh called - will preserve cursor on next version change');
        };
      }
    }
  }, [editor, readOnly, editorRef]);
  
  // Keyboard shortcut for Find (Ctrl+F / Cmd+F)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+F or Cmd+F to open search
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        setShowSearch(true);
      }
      // Escape to close search (only if search is open)
      if (e.key === 'Escape' && showSearch) {
        setShowSearch(false);
        // Clear search when closing
        if (editor) {
          editor.commands.setSearchTerm('');
        }
      }
    };
    
    window.document.addEventListener('keydown', handleKeyDown);
    return () => window.document.removeEventListener('keydown', handleKeyDown);
  }, [showSearch, editor]);
  
  // Handle external document updates (from AI operations)
  // CRITICAL: Only depend on document.version, not the entire document object
  // to avoid resetting editor when parent re-renders with same data
  const currentVersion = document.version;
  useEffect(() => {
    if (editor && document && currentVersion !== documentVersion) {
      // Check if we should skip this refresh based on the unified sync state
      const skipReason = skipRefreshReasonRef.current;
      if (skipReason) {
        const reasonText = skipReason === 'section_update'
          ? 'section_update already applied content'
          : 'local save version change (preserving cursor)';
        logger.debug(`[StructuredEditor] Skipping editor refresh - ${reasonText}`);
        logger.debug('[StructuredEditor] Version update:', documentVersion, '→', currentVersion);
        skipRefreshReasonRef.current = null;  // Clear for next version change
        setDocumentVersion(currentVersion);
        setLastSavedVersion(currentVersion);
        return;
      }
      
      // Set flag to prevent triggering onUpdate during external changes
      logger.debug('[StructuredEditor] Setting isUpdatingFromPropRef=true (document version changed)');
      isUpdatingFromPropRef.current = true;
      
      // Update editor content when document changes (e.g., after AI operations applied)
      logger.debug('[StructuredEditor] Document version changed:', documentVersion, '→', currentVersion);
      logger.debug('[StructuredEditor] Refreshing editor with new content from server');
      const newContent = convertDocumentToTipTap(document);
      
      // Use setContent with emitUpdate=false to prevent triggering onUpdate
      editor.commands.setContent(newContent, false);
      
      setLastSavedVersion(currentVersion);
      setDocumentVersion(currentVersion);
      logger.debug('[StructuredEditor] Editor refreshed successfully with', document.body?.length, 'sections');
      
      // Reset flag after a longer delay to ensure all TipTap events have processed
      // This prevents auto-save from being triggered by the refresh
      setTimeout(() => {
        isUpdatingFromPropRef.current = false;
        logger.debug('[StructuredEditor] External update flag reset');
      }, 500);
    }
  }, [currentVersion, editor, documentVersion, document]);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);
  
  const handleSave = () => {
    if (editor) {
      // Convert current TipTap content to AeroDocument and save
      try {
        const _updatedDocument = convertTipTapToDocument(editor, document);
        logger.debug('[StructuredEditor] Manual save triggered');
        // Call parent's save handler
        onSave();
      } catch (error) {
        logger.error('[StructuredEditor] Error saving document:', error);
      }
    }
  };
  
  if (!editor) {
    return <div className="loading">Loading editor...</div>;
  }
  
  // Show error state for invalid documents
  if (isInvalidDocument) {
    logger.error('[StructuredEditor] Invalid document structure:', {
      hasDocument: !!document,
      hasMetadata: !!document?.metadata,
      hasBody: !!document?.body,
      documentNumber
    });
    return (
      <div className="flex items-center justify-center h-full bg-card">
        <div className="text-center p-8">
          <h2 className="text-xl font-semibold text-destructive mb-2">Invalid Document Structure</h2>
          <p className="text-muted-foreground">
            The document &quot;{documentNumber}&quot; has an invalid structure and cannot be displayed.
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            This may indicate the document file is corrupted or incomplete.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Document header bar */}
      {documentTitle && onBack && (
        <div className="bg-popover text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="text-white hover:text-muted-foreground">
              ← Back
            </button>
            <div>
              <h1 className="text-lg font-semibold">{documentTitle}</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {hasUnsavedChanges && (
              <span className="text-warning text-sm">Unsaved changes</span>
            )}
            {saving && (
              <span className="text-link text-sm">Saving...</span>
            )}
            {error && (
              <span className="text-destructive text-sm">{error}</span>
            )}
          </div>
        </div>
      )}
      
      {/* Toolbar - Fixed at top */}
      {!readOnly && (
        <div className="bg-card border-b border-border flex-shrink-0">
          <EditorToolbar 
            editor={editor} 
            document={document}
            onSave={handleSave}
            onToggleSplitView={onToggleSplitView}
            splitViewEnabled={splitViewEnabled}
            saving={saving}
            lastSaved={lastSaved}
            isOnline={isOnline}
            onToggleVersionHistory={projectId && documentNumber ? () => setShowVersionHistory(!showVersionHistory) : undefined}
            versionHistoryEnabled={showVersionHistory}
            projectId={projectId}
            documentNumber={documentNumber}
            isAIEditing={isAIEditing}
            showBlockIds={showBlockIds}
            onToggleBlockIds={() => setShowBlockIds(!showBlockIds)}
            onOpenPdfInViewer={onOpenPdfInViewer}
            onOpenSearch={() => setShowSearch(true)}
          />
        </div>
      )}
      
      {/* Undo All AI Changes button - appears after AI edits */}
      {preAIVersionId && projectId && documentNumber && !isAIEditing && (
        <div className="bg-warning-light border-b border-warning dark:border-warning px-4 py-2">
          <div className="flex items-center justify-between">
            <span className="text-warning text-sm">
              AI made changes to this document
            </span>
            <div className="flex items-center">
              <button
                onClick={async () => {
                  if (!preAIVersionId || !projectId || !documentNumber) return;
                  try {
                    setIsRestoringVersion(true);
                    setRestoreError(null);
                    logger.debug('[StructuredEditor] Restoring to pre-AI version:', preAIVersionId);
                    await documentVersionApi.restoreVersion(projectId, documentNumber, preAIVersionId);
                    setPreAIVersionId(null);
                    preAIContentRef.current = null;
                    // Reload the document to get the restored version
                    window.location.reload();
                  } catch (err: unknown) {
                    logger.error('[StructuredEditor] Failed to restore version:', err);
                    setRestoreError(err instanceof Error ? err.message : 'Failed to restore document. Please try again.');
                  } finally {
                    setIsRestoringVersion(false);
                  }
                }}
                disabled={isRestoringVersion}
                className="px-3 py-1 bg-warning-light0 hover:bg-warning text-white text-sm font-medium rounded transition-colors disabled:opacity-50"
              >
                {isRestoringVersion ? 'Restoring...' : 'Undo All AI Changes'}
              </button>
              <button
                onClick={() => {
                  setPreAIVersionId(null);
                  preAIContentRef.current = null;
                  setRestoreError(null);
                }}
                className="ml-2 text-warning hover:text-warning dark:hover:text-link-hover"
                title="Dismiss"
              >
                ✕
              </button>
            </div>
          </div>
          {restoreError && (
            <div className="mt-2 text-destructive text-sm">
              {restoreError}
            </div>
          )}
        </div>
      )}
      
      {/* Content area with sidebar and editor */}
      <div className="flex flex-1 min-h-0">
        {/* Sidebar with TOC navigation */}
        <EditorSidebar document={document} editor={editor} onAddSection={onAddSection} />
        
        {/* Editor content */}
        <div className="flex-1 overflow-y-auto bg-card">
          <div className="w-full px-4 py-8 pb-32">
            {/* Document metadata header (read-only) */}
            <div className="mb-8 p-4 bg-muted border border-border rounded">
              <h1 className="text-2xl font-bold text-link mb-2">
                {document.metadata.documentNumber}
              </h1>
              <h2 className="text-xl text-foreground mb-4">
                {document.metadata.projectSpecificTitle || document.metadata.documentTitle}
              </h2>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-semibold">Revision:</span>{' '}
                  {document.metadata.status === 'Draft' && !readOnly ? (
                    <button
                      type="button"
                      className="text-link hover:underline cursor-pointer"
                      onClick={() => {
                        setOverrideRevision(document.metadata.currentRevision || 'IR');
                        setShowRevisionOverride(true);
                      }}
                      title="Click to change revision level"
                    >
                      {document.metadata.currentRevision}
                    </button>
                  ) : (
                    document.metadata.currentRevision
                  )}
                </div>
                <div>
                  <span className="font-semibold">Status:</span>{' '}
                  <span className={`badge ${getStatusBadgeClass(document.metadata.status)}`}>
                    {document.metadata.status}
                  </span>
                </div>
                <div>
                  <span className="font-semibold">Project:</span> {document.metadata.projectCode}
                </div>
                <div>
                  <span className="font-semibold">Date:</span>{' '}
                  {new Date(document.metadata.revisionDate).toLocaleDateString()}
                </div>
              </div>
            </div>
            
            {/* Find and Replace Bar */}
            {showSearch && editor && (
              <EditorSearch 
                editor={editor}
                onClose={() => {
                  setShowSearch(false);
                  editor.commands.setSearchTerm('');
                }}
              />
            )}
            
            {/* Editor */}
            <div 
              ref={editorContainerRef}
              className={`prose max-w-none ${showBlockIds ? 'show-block-ids' : ''}`}
              onClick={(e) => {
                if (!showBlockIds) return;
                
                // Get click position relative to the editor container
                const container = editorContainerRef.current;
                if (!container) return;
                
                const containerRect = container.getBoundingClientRect();
                const clickXInContainer = e.clientX - containerRect.left;
                const clickY = e.clientY;
                
                // The editor has padding-left: 80px when block IDs are shown
                // Block ID badges are in the first 80px (the margin area)
                // Only trigger on clicks in the margin area
                if (clickXInContainer > 80) {
                  return;
                }
                
                // Find all block elements and check which one contains the click Y position
                const blockElements = container.querySelectorAll('[data-block-id], [id^="sec-"], h1[id], h2[id], h3[id], h4[id], h5[id], h6[id]');
                
                let matchedBlock: Element | null = null;
                for (const el of blockElements) {
                  const rect = el.getBoundingClientRect();
                  // Check if click Y is within this element's vertical bounds
                  if (clickY >= rect.top && clickY <= rect.bottom) {
                    matchedBlock = el;
                    break;
                  }
                }
                
                if (matchedBlock) {
                  const blockId = matchedBlock.getAttribute('data-block-id') || matchedBlock.getAttribute('id');
                  if (blockId) {
                    // Dispatch custom event for WorkspacePage to handle
                    const event = new CustomEvent('block-id-clicked', { 
                      detail: { blockId },
                      bubbles: true 
                    });
                    window.document.dispatchEvent(event);
                    
                    // Prevent text selection
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }
              }}
            >
              <EditorContent editor={editor} />
              {editor && <TableBubbleMenu editor={editor} />}
            </div>
          </div>
        </div>
        
        {/* Version History Sidebar */}
        {showVersionHistory && !compareVersions && projectId && documentNumber && (
          <div className="w-80 flex-shrink-0">
            <VersionHistory
              projectId={projectId}
              documentNumber={documentNumber}
              currentRevision={document.metadata.currentRevision}
              onCompare={(v1, v2) => {
                logger.debug('[StructuredEditor] Compare versions:', v1, v2);
                setCompareVersions({ v1, v2 });
                setShowVersionHistory(false);
              }}
              onRestore={async (revision) => {
                try {
                  logger.debug('[StructuredEditor] Restore to revision:', revision);
                  await documentsApi.restoreVersion(projectId, documentNumber, revision);
                  
                  // Reload the document to show restored content
                  window.location.reload();
                } catch (err) {
                  logger.error('[StructuredEditor] Restore failed:', err);
                  showError('Restore Failed', 'Failed to restore version. Please try again.');
                }
              }}
              onClose={() => setShowVersionHistory(false)}
            />
          </div>
        )}
      </div>
      
      {/* Version Comparison Overlay */}
      {compareVersions && projectId && documentNumber && (
        <div className="absolute inset-0 z-50 bg-card">
          <VersionCompare
            projectId={projectId}
            documentNumber={documentNumber}
            version1={compareVersions.v1}
            version2={compareVersions.v2}
            onClose={() => {
              setCompareVersions(null);
              setShowVersionHistory(true); // Re-open version history
            }}
          />
        </div>
      )}

      {/* Revision Override Dialog */}
      <Dialog open={showRevisionOverride} onOpenChange={(open) => {
        if (!open) setShowRevisionOverride(false);
      }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Change Revision Level</DialogTitle>
            <DialogDescription>
              Set the revision level for this draft document.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="revision-override">Revision</Label>
              <RadixSelect
                value={overrideRevision}
                onValueChange={setOverrideRevision}
              >
                <SelectTrigger id="revision-override">
                  <SelectValue placeholder="Select revision..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="IR">IR - Initial Release</SelectItem>
                  {Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)).map(letter => (
                    <SelectItem key={letter} value={letter}>{letter}</SelectItem>
                  ))}
                </SelectContent>
              </RadixSelect>
              <p className="text-xs text-muted-foreground">
                IR = Initial Release (default for new documents). Use a letter revision when importing an existing document.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRevisionOverride(false)}
              disabled={isOverridingRevision}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRevisionOverride}
              disabled={isOverridingRevision || overrideRevision === document.metadata.currentRevision}
            >
              {isOverridingRevision ? 'Saving...' : 'Change Revision'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Conversion functions are now imported from ../../utils/document-to-tiptap
